import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiX, HiUser, HiCheckCircle, HiCamera } from "react-icons/hi";
import { FaFlask, FaBuilding } from "react-icons/fa";

import useOutsideClick from "../../hooks/useOutsideClick";
import {
  selectGeneral,
  setShowRegisterModal,
} from "../../redux/slices/generalSlice";
import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";

/**
 * RegisterModal Component (Simplified)
 * 
 * Step 1 of registration: Collects mandatory fields
 * - Name (first and last)
 * - Role (applicant or organization)
 * 
 * After this, user will be redirected to complete-profile page.
 */
const RegisterModal = () => {
  const dispatch = useDispatch();
  const { showRegisterModal, registerEmail, registerPassword, googleUserData } = useSelector(selectGeneral);
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstname: googleUserData?.firstname || "",
    lastname: googleUserData?.lastname || "",
    role: "",
    fundername: "",
    profile_image: googleUserData?.profile_image || null,
  });
  const [error, setError] = useState("");
  const [imagePreview, setImagePreview] = useState(googleUserData?.profile_image || null);

  // Update form when Google data changes
  useEffect(() => {
    if (googleUserData) {
      setFormData(prev => ({
        ...prev,
        firstname: googleUserData.firstname || prev.firstname,
        lastname: googleUserData.lastname || prev.lastname,
        profile_image: googleUserData.profile_image || prev.profile_image,
      }));
      setImagePreview(googleUserData.profile_image || null);
    }
  }, [googleUserData]);

  const handleClose = () => {
    dispatch(setShowRegisterModal({ show: false }));
    setFormData({ firstname: "", lastname: "", role: "", fundername: "" });
    setError("");
  };

  useOutsideClick(modalRef, handleClose, null, showRegisterModal);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const requestBody = {
        email: registerEmail,
        password: registerPassword,
        role: formData.role,
        profile_image: formData.profile_image,
      };

      if (formData.role === "applicant") {
        requestBody.firstname = formData.firstname;
        requestBody.lastname = formData.lastname;
      } else {
        requestBody.fundername = formData.fundername;
      }

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message);
        setIsLoading(false);
        return;
      }

      // Store token and redirect to profile completion
      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.user.role);
      localStorage.setItem("auth_provider", "email");
      
      handleClose();
      window.location.href = "/create-profile";
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleRoleSelect = (role) => {
    setFormData({ ...formData, role });
    setError("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError("Image must be less than 10MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData({ ...formData, profile_image: reader.result });
        setError("");
      };
      reader.readAsDataURL(file);
    }
  };

  // Body overflow control
  useEffect(() => {
    if (showRegisterModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showRegisterModal]);

  if (!showRegisterModal) return null;

  return (
    <div className="min-w-[100vw] w-full h-screen flex justify-center items-center bg-secondary/80 backdrop-blur-md fixed top-0 left-0 z-50 p-4">
      <div
        ref={modalRef}
        className="w-full max-w-md flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl transform transition-all animate-fadeIn"
      >
        {/* Header */}
        <div className="relative w-full p-8 pb-4 text-center">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-200"
          >
            <HiX className="text-gray-600" size={18} />
          </button>

          <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-primary-green/10 items-center justify-center mb-4">
            <HiUser className="text-primary" size={28} />
          </div>
          <h2 className="text-2xl font-bold text-secondary mb-2">
            Complete Your Profile
          </h2>
          <p className="text-gray-500 text-sm">
            Just a few more details to get started
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="w-full px-8 py-4 space-y-4">
          {/* Role Selection - First! */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">I am a...</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleRoleSelect("applicant")}
                className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200 ${
                  formData.role === "applicant"
                    ? "border-primary bg-primary/5 shadow-md"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                  formData.role === "applicant" ? "bg-primary text-white" : "bg-gray-100 text-gray-500"
                }`}>
                  <FaFlask size={20} />
                </div>
                <span className={`font-medium ${formData.role === "applicant" ? "text-primary" : "text-gray-700"}`}>
                  Researcher
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  Individual scientist
                </span>
                {formData.role === "applicant" && (
                  <HiCheckCircle className="text-primary mt-2" size={20} />
                )}
              </button>

              <button
                type="button"
                onClick={() => handleRoleSelect("organization")}
                className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200 ${
                  formData.role === "organization"
                    ? "border-primary bg-primary/5 shadow-md"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                  formData.role === "organization" ? "bg-primary text-white" : "bg-gray-100 text-gray-500"
                }`}>
                  <FaBuilding size={20} />
                </div>
                <span className={`font-medium ${formData.role === "organization" ? "text-primary" : "text-gray-700"}`}>
                  Organization
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  Lab, institution, DAO
                </span>
                {formData.role === "organization" && (
                  <HiCheckCircle className="text-primary mt-2" size={20} />
                )}
              </button>
            </div>
          </div>

          {/* Conditional Fields Based on Role */}
          {formData.role && (
            <>
              {/* Avatar Upload */}
              <div className="flex flex-col items-center mb-2">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-gray-100 group-hover:ring-primary/30 transition-all">
                    <img
                      src={imagePreview || UserPlaceHolderImg}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-primary hover:bg-primary/90 rounded-full flex items-center justify-center shadow-lg transition-all"
                  >
                    <HiCamera className="text-white" size={16} />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">Click camera to change photo</p>
              </div>

              {/* Name Fields - for Researchers */}
              {formData.role === "applicant" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              )}

              {/* Organization Name - for Organizations */}
              {formData.role === "organization" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                  <input
                    type="text"
                    name="fundername"
                    value={formData.fundername}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                    placeholder="Research Lab, DAO, or Institution"
                  />
                </div>
              )}
            </>
          )}

          {error && (
            <p className="text-red-500 text-sm text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={isLoading || !formData.role}
            className={`w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-primary via-[#8a5cff] to-[#7e7cff] hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all duration-300 ${
              isLoading || !formData.role ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            <span>{isLoading ? "Creating Account..." : "Continue"}</span>
          </button>
        </form>

        {/* Step Indicator */}
        <div className="w-full px-8 pb-6">
          <div className="flex items-center justify-center gap-2">
            <div className="w-8 h-1 rounded-full bg-primary"></div>
            <div className="w-8 h-1 rounded-full bg-gray-200"></div>
          </div>
          <p className="text-center text-xs text-gray-400 mt-2">Step 1 of 2</p>
        </div>
      </div>
    </div>
  );
};

export default RegisterModal;
